import {
  Badge,
  Button,
  Card,
  ErrorBox,
  LineChart,
  Link,
  Note,
  Page,
  Select,
  Stat,
  Table,
  Td,
} from '@elsa/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fmtDateTime, fmtDay, fmtLei, fmtNum, fmtShare, plural } from '../lib/format.ts';
import { rpc } from '../lib/supabase.ts';
import type { AcqReport, AlertReportData, ReportAcq } from '../lib/types.ts';
import { markRead } from './AppLayout.tsx';
import { Refreshing, SkeletonCards, SkeletonTable } from './loading.tsx';
import { Bar, NotLinked } from './Sales.tsx';

// Raportul unei alerte (docs/28): o notificare pe alerta si zi, cu achizitiile directe noi din
// noaptea trecuta. Cifrele, ce a castigat firma, clientii ei, clasamentele si, la final, lista
// achizitiilor. Totul prin RLS (app.alert_report, app.alert_report_acquisitions): pe ce vede acum
// utilizatorul (istoricul planului, codurile firmei, judetele lui).

const PAGE = 50;

/** Numele alertei din titlul notificarii („Seringi: 63 de achiziții noi”). */
const alertName = (r: AlertReportData) =>
  r.watchlist?.name ?? r.title.replace(/: [0-9.]+ (de )?achizi.*$/, '');

/** Zilele achizitiilor: o zi sau un interval. */
function daysLabel(t: AcqReport['totals']): string {
  if (!t.last_day) return '';
  return t.first_day && t.first_day !== t.last_day
    ? `finalizate între ${fmtDay(t.first_day)} și ${fmtDay(t.last_day)}`
    : `finalizate pe ${fmtDay(t.last_day)}`;
}

/** Pragurile in lei intregi („sub 10.000 lei”, „10.000 – 50.000 lei”). */
const leiInt = (v: number) => `${fmtNum(v)} lei`;
const bucketLabel = (b: AcqReport['by_value'][number]) =>
  !b.min
    ? `sub ${leiInt(b.max ?? 0)}`
    : b.max === null
      ? `peste ${leiInt(b.min)}`
      : `${fmtNum(b.min)} – ${leiInt(b.max)}`;

/** Diferenta fata de medie, in procente cu semn („+12%”, „−5%”). */
function delta(cur: number, avg: number): string {
  if (!avg) return '—';
  const p = ((cur - avg) / avg) * 100;
  const s = Math.abs(p).toLocaleString('ro-RO', { maximumFractionDigits: 0 });
  return `${p > 0.5 ? '+' : p < -0.5 ? '−' : ''}${s}%`;
}

const mean = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);

function Authority({ id, name }: { id: number | null; name: string | null }) {
  if (!id) return <>{name ?? '—'}</>;
  return (
    <Link to={`/app/autoritati/${id}`} className="font-medium hover:underline">
      {name ?? `Instituția ${id}`}
    </Link>
  );
}

function Supplier({ id, name, ours }: { id: number | null; name: string | null; ours: boolean }) {
  return (
    <>
      {id ? (
        <Link to={`/app/furnizori/${id}`} className="hover:underline">
          {name ?? `Furnizorul ${id}`}
        </Link>
      ) : (
        (name ?? '—')
      )}
      {ours ? (
        <>
          {' '}
          <Badge tone="good">voi</Badge>
        </>
      ) : null}
    </>
  );
}

/** Fata de rapoartele anterioare ale aceleiasi alerte (ultimele 30). */
function Trend({ r }: { r: AlertReportData }) {
  const t = r.report.totals;
  const counts = r.history.map((h) => Number(h.count ?? 0));
  const values = r.history.filter((h) => h.value_ron !== null).map((h) => Number(h.value_ron));
  const points = [
    ...r.history.map((h) => [Date.parse(h.created_at) / 1000, Number(h.count ?? 0)] as const),
    [Date.parse(r.created_at) / 1000, t.acquisitions] as const,
  ].map(([x, y]) => [x, y] as [number, number]);
  return (
    <Card title="Față de rapoartele anterioare">
      <p className="text-sm text-gri-800">
        Media ultimelor {plural(r.history.length, 'raport', 'rapoarte')} ale alertei:{' '}
        <b>{plural(Math.round(mean(counts)), 'achiziție', 'achiziții')}</b>
        {values.length ? (
          <>
            {' '}
            și <b>{fmtLei(Math.round(mean(values)))}</b>
          </>
        ) : null}
        . Acum: <b>{delta(t.acquisitions, mean(counts))}</b> achiziții
        {values.length ? (
          <>
            , <b>{delta(Number(t.value_ron), mean(values))}</b> valoare
          </>
        ) : null}
        .
      </p>
      {points.length > 1 ? (
        <div className="mt-3">
          <LineChart
            series={[{ name: 'achiziții', color: '#444141', points }]}
            formatX={(x) => fmtDay(new Date(x * 1000).toISOString())}
            height={110}
          />
        </div>
      ) : null}
    </Card>
  );
}

function Acquisitions({ id, total }: { id: number; total: number }) {
  const [filter, setFilter] = useState<'all' | 'ours' | 'others'>('all');
  const [sort, setSort] = useState<'value' | 'day'>('value');
  const [page, setPage] = useState(0);
  const q = useQuery({
    queryKey: ['alert_report_acquisitions', id, filter, sort, page],
    queryFn: () =>
      rpc<{ total: number; rows: ReportAcq[] } | null>('alert_report_acquisitions', {
        p_id: id,
        p_filter: filter,
        p_sort: sort,
        p_limit: PAGE,
        p_offset: page * PAGE,
      }),
    placeholderData: (prev) => prev,
  });
  const n = q.data?.total ?? 0;
  const from = page * PAGE;
  return (
    <Card
      title={`Achizițiile (${fmtNum(total)})`}
      actions={
        <div className="flex flex-wrap gap-2">
          <div className="w-44">
            <Select
              aria-label="Care achiziții"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as typeof filter);
                setPage(0);
              }}
            >
              <option value="all">Toate</option>
              <option value="ours">Câștigate de voi</option>
              <option value="others">Câștigate de alții</option>
            </Select>
          </div>
          <div className="w-36">
            <Select
              aria-label="Ordinea"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as typeof sort);
                setPage(0);
              }}
            >
              <option value="value">După valoare</option>
              <option value="day">După zi</option>
            </Select>
          </div>
        </div>
      }
    >
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : (
        <Refreshing active={q.isFetching}>
          <Table
            head={['Achiziția', 'Instituția', 'Furnizorul', 'Ziua', 'Valoare']}
            empty={!q.data?.rows.length}
          >
            {q.data?.rows.map((a) => (
              <tr key={a.id}>
                <Td className="max-w-xs">
                  <Link to={`/app/achizitii/${a.id}`} className="font-medium hover:underline">
                    {a.name ?? `Achiziția ${a.id}`}
                  </Link>
                  {a.cpv_code ? <div className="text-xs text-gri-700">{a.cpv_code}</div> : null}
                </Td>
                <Td className="max-w-xs">
                  <Authority id={a.authority_id} name={a.authority} />
                  {a.county ? <div className="text-xs text-gri-700">{a.county}</div> : null}
                </Td>
                <Td className="max-w-xs">
                  <Supplier id={a.supplier_id} name={a.supplier} ours={a.ours} />
                </Td>
                <Td className="whitespace-nowrap tabular-nums">{fmtDay(a.day)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(a.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Refreshing>
      )}
      {n > PAGE ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-gri-700">
            {fmtNum(from + 1)}–{fmtNum(Math.min(from + PAGE, n))} din {fmtNum(n)}
          </span>
          <div className="flex gap-2">
            <Button disabled={!page} onClick={() => setPage(page - 1)}>
              Înapoi
            </Button>
            <Button disabled={from + PAGE >= n} onClick={() => setPage(page + 1)}>
              Înainte
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function Report({ r }: { r: AlertReportData }) {
  const s = r.report;
  const t = s.totals;
  const o = s.ours;
  const c = s.clients;
  const maxCounty = Math.max(0, ...s.by_county.map((x) => Number(x.value_ron)));
  const maxBucket = Math.max(0, ...s.by_value.map((x) => x.acquisitions));
  const hidden = r.listed - t.acquisitions;
  return (
    <div className="space-y-4">
      <p className="-mt-2 text-xs text-gri-700">
        Raportul din {fmtDateTime(r.created_at)}
        {t.last_day ? ` · achiziții ${daysLabel(t)}` : ''}
        {r.organization ? ` · ${r.organization.name}` : ''}
        {r.watchlist ? ` · alertă ${r.watchlist.scope === 'team' ? 'a firmei' : 'personală'}` : ''}
      </p>
      {!r.supplier ? <NotLinked /> : null}
      {r.count > r.listed ? (
        <Note>
          Alerta a găsit {plural(r.count, 'achiziție', 'achiziții')}; raportul cuprinde primele{' '}
          {fmtNum(r.listed)}.
        </Note>
      ) : null}
      {hidden > 0 ? (
        <Note>
          {plural(
            hidden,
            'achiziție din raport nu se mai vede',
            'achiziții din raport nu se mai văd',
          )}{' '}
          (istoricul planului sau codurile CPV ale firmei s-au schimbat de atunci).
        </Note>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Achiziții noi"
          value={fmtNum(t.acquisitions)}
          sub={`${plural(t.authorities, 'instituție', 'instituții')} · ${plural(t.suppliers, 'furnizor', 'furnizori')}`}
        />
        <Stat
          label="Valoare totală"
          value={fmtLei(t.value_ron)}
          sub={`medie ${fmtLei(t.avg_ron)} · mediană ${fmtLei(t.median_ron)}`}
        />
        {o ? (
          <Stat
            label="Câștigate de voi"
            value={fmtNum(o.acquisitions)}
            sub={`${fmtLei(o.value_ron)} · ${fmtShare(o.value_ron, t.value_ron)} din valoare`}
            accent
          />
        ) : (
          <Stat label="Cea mai mare" value={fmtLei(t.max_ron)} />
        )}
        {o ? (
          <Stat
            label="Locul vostru"
            value={o.rank ? `${fmtNum(o.rank)} din ${fmtNum(t.suppliers)}` : '—'}
            sub={o.rank ? 'între furnizori, după valoare' : 'n-ați câștigat niciuna'}
          />
        ) : (
          <Stat label="Furnizori" value={fmtNum(t.suppliers)} />
        )}
      </div>

      {c?.acquisitions ? (
        <Card title="La clienții voștri">
          <p className="text-sm text-gri-800">
            {plural(c.authorities, 'instituție', 'instituții')} din raport{' '}
            {c.authorities === 1 ? 'a mai cumpărat' : 'au mai cumpărat'} de la voi:{' '}
            <b>{plural(c.acquisitions, 'achiziție', 'achiziții')}</b> ({fmtLei(c.value_ron)}),{' '}
            {c.lost_acquisitions ? (
              <>
                din care{' '}
                <b className="text-semnal-700">
                  {fmtNum(c.lost_acquisitions)} câștigate de alții ({fmtLei(c.lost_value)})
                </b>
                . Le găsiți marcate „client” la instituții.
              </>
            ) : (
              'toate câștigate de voi.'
            )}
          </p>
        </Card>
      ) : null}

      {r.history.length ? <Trend r={r} /> : null}

      <div className="grid gap-3 lg:grid-cols-2 [&>*]:min-w-0">
        <Card title="Instituțiile">
          <Table head={['Instituția', 'Achiziții', 'Valoare']} empty={!s.top_authorities.length}>
            {s.top_authorities.map((a, i) => (
              <tr key={a.id ?? `t${i}`}>
                <Td className="max-w-xs">
                  <Authority id={a.id} name={a.name} />
                  <div className="text-xs text-gri-700">
                    {a.county}
                    {a.client ? (
                      <>
                        {' '}
                        <Badge tone="info">client</Badge>
                      </>
                    ) : null}
                  </div>
                </Td>
                <Td className="tabular-nums">
                  {fmtNum(a.acquisitions)}
                  {a.ours ? (
                    <div className="text-xs text-gri-700">
                      {a.ours === 1 ? '1 a voastră' : `${fmtNum(a.ours)} ale voastre`}
                    </div>
                  ) : null}
                </Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(a.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Furnizorii">
          <Table head={['#', 'Furnizorul', 'Achiziții', 'Valoare']} empty={!s.top_suppliers.length}>
            {s.top_suppliers.map((x, i) => (
              <tr key={x.id ?? `s${i}`} className={x.ours ? 'bg-semnal-50' : ''}>
                <Td className="tabular-nums text-gri-700">{x.rank}</Td>
                <Td className="max-w-xs">
                  <Supplier id={x.id} name={x.name} ours={x.ours} />
                  <div className="text-xs text-gri-700">
                    {plural(x.authorities, 'instituție', 'instituții')}
                  </div>
                </Td>
                <Td className="tabular-nums">{fmtNum(x.acquisitions)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(x.value_ron)}</Td>
              </tr>
            ))}
          </Table>
          {o && (o.acquisitions ?? 0) > 0 && !s.top_suppliers.some((x) => x.ours) ? (
            <p className="mt-2 text-xs text-gri-700">
              Voi: locul {fmtNum(o.rank)}, {plural(o.acquisitions, 'achiziție', 'achiziții')},{' '}
              {fmtLei(o.value_ron)}.
            </p>
          ) : null}
        </Card>
      </div>

      <div className="grid gap-3 lg:grid-cols-2 [&>*]:min-w-0">
        <Card title="Pe județe">
          <Table head={['Județul', 'Instituții', 'Valoare']} empty={!s.by_county.length}>
            {s.by_county.map((x) => (
              <tr key={x.county}>
                <Td className="w-1/2">
                  {x.county}
                  <Bar value={Number(x.value_ron)} max={maxCounty} />
                </Td>
                <Td className="tabular-nums">{fmtNum(x.authorities)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">
                  {fmtLei(x.value_ron)}
                  <div className="text-xs text-gri-700">
                    {plural(x.acquisitions, 'achiziție', 'achiziții')}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Pe valori">
          <Table head={['Valoarea achiziției', 'Achiziții', 'Valoare']}>
            {s.by_value.map((b) => (
              <tr key={b.min}>
                <Td className="w-1/2 whitespace-nowrap">
                  {bucketLabel(b)}
                  <Bar value={b.acquisitions} max={maxBucket} />
                </Td>
                <Td className="tabular-nums">{fmtNum(b.acquisitions)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(b.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      <div className="grid gap-3 lg:grid-cols-2 [&>*]:min-w-0">
        <Card title="Codurile CPV">
          <Table head={['CPV', 'Achiziții', 'Valoare']} empty={!s.top_cpv.length}>
            {s.top_cpv.map((x) => (
              <tr key={x.code}>
                <Td className="max-w-xs">
                  <Link to={`/app/preturi/${x.code}`} className="font-medium hover:underline">
                    {x.code}
                  </Link>
                  <div className="text-xs text-gri-700">{x.label}</div>
                </Td>
                <Td className="tabular-nums">{fmtNum(x.acquisitions)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(x.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Cele mai mari achiziții">
          <Table head={['Achiziția', 'Valoare']} empty={!s.largest.length}>
            {s.largest.map((a) => (
              <tr key={a.id}>
                <Td className="max-w-sm">
                  <Link to={`/app/achizitii/${a.id}`} className="font-medium hover:underline">
                    {a.name ?? `Achiziția ${a.id}`}
                  </Link>
                  <div className="text-xs text-gri-700">
                    {a.authority ?? '—'} · {a.supplier ?? '—'}
                    {a.ours ? (
                      <>
                        {' '}
                        <Badge tone="good">voi</Badge>
                      </>
                    ) : null}
                  </div>
                </Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(a.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>

      {s.by_day.length > 1 ? (
        <Card title="Pe zile">
          <Table head={['Ziua', 'Achiziții', 'Valoare']}>
            {s.by_day.map((d) => (
              <tr key={d.day}>
                <Td className="whitespace-nowrap">{fmtDay(d.day)}</Td>
                <Td className="tabular-nums">{fmtNum(d.acquisitions)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(d.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}

      <Acquisitions id={r.id} total={t.acquisitions} />
    </div>
  );
}

export function AlertReportPage({ id }: { id: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['alert_report', id],
    queryFn: () => rpc<AlertReportData | null>('alert_report', { p_id: id }),
  });
  const unread = q.data && !q.data.read_at;
  useEffect(() => {
    if (!unread) return;
    void markRead([id]).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }));
  }, [unread, id, qc]);
  const r = q.data;
  return (
    <Page
      title={r ? `Raport: ${alertName(r)}` : 'Raportul alertei'}
      actions={
        <Link to="/app/notificari" className="text-sm underline">
          Toate notificările
        </Link>
      }
    >
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <>
          <SkeletonCards count={4} />
          <SkeletonTable rows={6} cols={3} />
        </>
      ) : null}
      {q.data === null ? (
        <p className="text-sm text-gri-700">
          Raportul nu există sau nu e al tău.{' '}
          <Link to="/app/notificari" className="underline">
            Înapoi la notificări
          </Link>
          .
        </p>
      ) : null}
      {r ? <Report r={r} /> : null}
    </Page>
  );
}
